import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Agreement : famille "accord juridique formalisé".
 *
 * Accord juridique formalisé entre l'organisation et un tiers, avec clauses,
 * dates d'effet, échéances et obligations (mandat, bail, convention, contrat
 * cadre, adoption). Construite sur le modèle "famille + type" de la bible
 * (patron : `asset`).
 *
 * Le socle ne connaît pas d'accord concret ("mandat de vente", "bail
 * commercial") : il connaît la famille générique `agreement`. Le vocabulaire
 * métier est porté par le champ `type` (chaîne libre déclarée par l'app), qui
 * est le `kind` de la fiche renommé selon la convention du socle.
 *
 * Le tiers signataire (`contactId`) est le CŒUR de la famille : un accord existe
 * toujours entre l'organisation et une contrepartie, donc `contactId` est requis
 * (même logique que order.contactId ou registration.eventId/contactId). Les
 * autres liens sont de simples ids optionnels (aucun import de schéma) :
 * `subscriptionId`, `opportunityId`, `listingId`, `invoiceId`, et la liste
 * `documentIds` (pièces jointes, références `document`).
 *
 * Accès : org-wide standard en v0 (même doctrine que asset). Rien de sensible
 * signalé sur cette famille.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille agreement (hors champs standard du socle). */
export const agreementCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "mandat", "bail", "convention"). C'est le `kind` de la fiche. */
  type: z.string().min(1),
  /** Tiers signataire de l'accord (référence `contact`), cœur de la famille : requis. */
  contactId: z.string().min(1),
  /** Date de signature, optionnel. */
  signedAt: z.date().optional(),
  /** Début d'effet de l'accord, optionnel. */
  effectiveFrom: z.date().optional(),
  /** Fin d'effet / échéance de l'accord, optionnel. */
  effectiveUntil: z.date().optional(),
  /** Conditions de renouvellement, texte libre (ex. "tacite, préavis 3 mois"), optionnel. */
  renewalTerms: z.string().min(1).optional(),
  /** Obligations / clauses (structure libre par entrée), optionnel. */
  obligations: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Pièces jointes rattachées (références `document`), optionnel. */
  documentIds: z.array(z.string()).optional(),
  /** Abonnement lié (référence `subscription`), optionnel. */
  subscriptionId: z.string().min(1).optional(),
  /** Piste commerciale d'origine (référence `opportunity`), optionnel. */
  opportunityId: z.string().min(1).optional(),
  /** Annonce liée (référence `listing`), optionnel. */
  listingId: z.string().min(1).optional(),
  /** Facture liée (référence `invoice`), optionnel. */
  invoiceId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille agreement (champs standard + champs métier). */
export const AgreementSchema = z.object({
  ...baseEntityFields,
  ...agreementCoreFields,
});

/** Type d'un agreement. */
export type Agreement = z.infer<typeof AgreementSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const AgreementCreateInputSchema = z.object({
  ...agreementCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un agreement. */
export type AgreementCreateInput = z.infer<typeof AgreementCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const AgreementUpdateInputSchema = z
  .object({ ...agreementCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un agreement. */
export type AgreementUpdateInput = z.infer<typeof AgreementUpdateInputSchema>;
